import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { DriverFilterData, User } from '../../../models';
import { Driver, UnifiedTreeItem } from '../../../models/driver.model';
import { AuthService } from '../../../services';
import { DriverService } from '../../../services/driver.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { isEmptyObject } from '../../../helpers/helpers';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DriverVehicleAssetIconDirective } from '../../../directives/driver-vehicle-asset-icon.directive';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-filter',
  standalone: true,
  imports: [
    TranslocoPipe,
    NgbModule,
    CommonModule,
    FormsModule,
    ScrollingModule,
    DriverVehicleAssetIconDirective,
  ],
  templateUrl: './driver-filter.component.html',
  styleUrl: './driver-filter.component.scss',
})
export class DriverFilterComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private driverService = inject(DriverService);
  private errorService = inject(ErrorHandlerService);

  @Input() filter: DriverFilterData;
  @Input() accountFilterChanged$: Observable<number[]>;
  @Output() filterChanged: EventEmitter<DriverFilterData> = new EventEmitter<DriverFilterData>();

  @ViewChild('dropdown') dropdown: ElementRef;
  @ViewChild('dropdownTrigger') dropdownButton: ElementRef;

  isLoading: boolean = false;
  driverName: string = '';

  user: User;
  driversTree: UnifiedTreeItem[] = [];
  filteredDriversTree: UnifiedTreeItem[] = [];
  searchTerm: string = '';

  selectedDrivers: Array<UnifiedTreeItem> = [];
  selectedDriversCount: number = 0;

  private cancelSubject: Subject<void> = new Subject<void>();

  private accountFilterSubscription: Subscription;
  selectedAccounts: Array<number> = [];

  ngOnInit(): void {
    this.init();
  }

  async init(): Promise<void> {
    this.user = this.authService.getUser();

    if (!this.accountFilterChanged$) {
      this.initDrivers();
      return;
    }

    this.accountFilterSubscription = this.accountFilterChanged$.subscribe(
      (accountIds) => {
        this.selectedAccounts = accountIds ?? [];
        this.initDrivers();
      }
    );
  }

  async initDrivers(): Promise<void> {
    await this.getUserDrivers();
  }

  ngOnDestroy(): void {
    if (this.accountFilterSubscription) {
      this.accountFilterSubscription.unsubscribe();
    }

    this.cancelSubject.next();
  }

  openDropdown(event: Event): void {
    event.stopPropagation();

    if (!this.dropdown.nativeElement.className.includes('show')) {
      this.dropdownButton.nativeElement.click();
    }
  }

  async getUserDrivers() {
    if (!!this.user) {
      this.isLoading = true;
      try {
        this.cancelSubject.next();
        const response = await this.driverService.getUserDrivers(
          this.selectedAccounts,
          this.cancelSubject
        );
        if (response.data) {
          this.setInitialSelectedDriver(response.data);
          this.driverService.setDrivers(response.data);

          this.selectedDrivers = [];
          this.filter.driverIds = [];

          this.driversTree = this.buildFlatAccountDriverTree(response.data);
          this.filteredDriversTree = this.driversTree;

          this.updateFilter();
          this.updateSelectedDriversCount();

          this.isLoading = false;
        }
      } catch (error: any) {
        this.errorService.handleError(error, {
          callback: () => {
            this.isLoading = false;
          },
        });
      }
    }
  }

  setInitialSelectedDriver(drivers: Driver[]) {
    if (this.user && drivers && isEmptyObject(this.filter)) {
      this.filter = {
        driverIds: []
      };
    }
  }

  async onCheckboxChange(
    item: UnifiedTreeItem & { checked?: boolean }
  ): Promise<void> {
    this.toggleCheckBox(item);
    this.filter.driverIds = this.selectedDrivers
      .filter((x) => x.type === 'driver')
      .map((x) => x.id);
    this.updateFilter();
    this.updateSelectedDriversCount();
  }

  searchDrivers(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    if (this.searchTerm) {
      const drivers = this.driverService.getDrivers();
      if (drivers && drivers.length > 0) {
        const hierarchicalTree = this.buildAccountDriverTree(drivers);
        this.filteredDriversTree = this.searchTreeItems(
          hierarchicalTree,
          this.searchTerm
        );
      } else {
        this.filteredDriversTree = [];
      }
    } else {
      this.filteredDriversTree = this.driversTree;
    }

    this.reloadSelectedDrivers();
  }

  reloadSelectedDrivers(): void {
    this.filteredDriversTree?.forEach((item) => {
      item.checked = !!this.selectedDrivers.find((x) => x.id == item.id);
    });

    this.updateSelectedDriversCount();
  }

  searchTreeItems(
    items: UnifiedTreeItem[],
    searchTerm: string,
    level: number = 0
  ): UnifiedTreeItem[] {
    let result: UnifiedTreeItem[] = [];

    for (const item of items) {
      const matched = item.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      let childResults: UnifiedTreeItem[] = [];

      if (item.children && item.children.length > 0) {
        childResults = this.searchTreeItems(
          item.children,
          searchTerm,
          level + 1
        );
      }

      if (matched) {
        item.hierarchyLevel = level;
        const flatChildren = this.flattenTreeForSearch(
          item.children || [],
          level + 1
        );
        result.push(item);
        result.push(...flatChildren);
      } else if (childResults.length > 0) {
        item.hierarchyLevel = level;
        result.push(item);
        result.push(...childResults);
      }
    }

    return result;
  }

  private flattenTreeForSearch(
    items: UnifiedTreeItem[],
    level: number
  ): UnifiedTreeItem[] {
    let result: UnifiedTreeItem[] = [];
    for (const item of items) {
      item.hierarchyLevel = level;
      result.push(item);
      if (item.children && item.children.length > 0) {
        result.push(...this.flattenTreeForSearch(item.children, level + 1));
      }
    }
    return result;
  }

  updateFilter(): void {
    this.filterChanged.next(this.filter);
  }

  private toggleCheckBox(item: UnifiedTreeItem) {
    const isChecked = !item.checked;
    item.checked = isChecked;

    if (isChecked) {
      this.selectedDrivers.push(item);
    } else {
      this.selectedDrivers = this.selectedDrivers.filter(
        (x) => x.id !== item.id
      );
    }

    const treeToUse = this.searchTerm
      ? this.filteredDriversTree
      : this.driversTree;
    const startIndex = treeToUse.findIndex((x) => x.key === item.key);

    for (let i = startIndex + 1; i < treeToUse.length; i++) {
      const current = treeToUse[i];

      if (current.hierarchyLevel <= item.hierarchyLevel) {
        break;
      }

      current.checked = isChecked;

      if (isChecked) {
        if (!this.selectedDrivers.find((x) => x.id === current.id)) {
          this.selectedDrivers.push(current);
        }
      } else {
        this.selectedDrivers = this.selectedDrivers.filter(
          (x) => x.id !== current.id
        );
      }
    }

    if (this.searchTerm) {
      const originalIndex = this.driversTree.findIndex(
        (x) => x.key === item.key
      );
      if (originalIndex !== -1) {
        for (let i = originalIndex + 1; i < this.driversTree.length; i++) {
          const current = this.driversTree[i];

          if (current.hierarchyLevel <= item.hierarchyLevel) {
            break;
          }

          current.checked = isChecked;
        }
      }
    }
  }

  private updateSelectedDriversCount(): void {
    this.selectedDriversCount = this.selectedDrivers.filter(
      (x) => x.type === 'driver'
    ).length;
  }

  buildFlatAccountDriverTree(drivers: Driver[]): UnifiedTreeItem[] {
    const flatList: UnifiedTreeItem[] = [];
    const rootAccounts: UnifiedTreeItem[] =
      this.buildAccountDriverTree(drivers);
    const addToFlatList = (item: UnifiedTreeItem, level: number = 0) => {
      item.hierarchyLevel = level;
      item.isParent = item.children?.length > 0;
      if (!item.isParent && item.name) {
        flatList.push(item);
      } else if (item.isParent) {
        flatList.push(item);
        for (const child of item.children) {
          addToFlatList(child, level + 1);
          item.children = [];
        }
      }
    };

    for (const root of rootAccounts) {
      addToFlatList(root, 0);
    }

    return flatList;
  }

  buildAccountDriverTree(drivers: Driver[]): UnifiedTreeItem[] {
    const accountMap = new Map<number, UnifiedTreeItem>();
    const childrenMap = new Map<number, UnifiedTreeItem[]>();

    for (const d of drivers) {
      if (!childrenMap.has(d.accountId)) {
        childrenMap.set(d.accountId, []);
      }

      if (d.driverId) {
        const driverItem = new UnifiedTreeItem({
          id: d.driverId,
          name: `${d.firstName} ${d.lastName}`,
          type: 'driver',
          hierarchyLevel: d.hierarchyLevel,
        });

        childrenMap.get(d.accountId)!.push(driverItem);
      }

      if (!accountMap.has(d.accountId)) {
        accountMap.set(
          d.accountId,
          new UnifiedTreeItem({
            id: d.accountId,
            name: d.accountName,
            type: 'account',
            hierarchyLevel: d.hierarchyLevel,
          })
        );
      }
    }

    const rootAccounts: UnifiedTreeItem[] = [];

    for (const [accountId, accountItem] of accountMap.entries()) {
      const matchingDriver = drivers.find((d) => d.accountId === accountId);
      if (!matchingDriver) continue;

      accountItem.children.push(...(childrenMap.get(accountId) || []));

      if (matchingDriver.accountParentId === 0) {
        rootAccounts.push(accountItem);
      } else {
        const parent = accountMap.get(matchingDriver.accountParentId);
        if (parent) {
          parent.children.push(accountItem);
        } else {
          rootAccounts.push(accountItem);
        }
      }
    }

    return rootAccounts;
  }
}
